import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
  Fade,
} from '@mui/material';
import {
  CardMembership,
  Groups,
  Flight,
  DirectionsBus,
  Build as BuildIcon,
  PhotoCamera as PhotoCameraIcon,
  Storefront,
  EmojiEvents,
  Speed,
  Timeline,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import hfssLogo from '../../assets/hfss_logo.png';
import RemindersModal from '../../components/modals/RemindersModal';
import ServiceAuthModal from '../../components/ServiceAuthModal';
import WindyWidget from '../../components/WindyWidget';
import { useUnifiedAuth } from '../../contexts/UnifiedAuthContext';

const LandingPage: React.FC = () => {
  // console.log('[LandingPage] Component mounting at path:', window.location.pathname);
  
  const navigate = useNavigate();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [remindersModalOpen, setRemindersModalOpen] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [serviceIntent, setServiceIntent] = useState<
    'shuttle' | 'equipment' | 'marketplace' | null
  >(null);
  
  // Use unified auth context to check authentication status
  const { isAuthenticated } = useUnifiedAuth();
  
  React.useEffect(() => {
    // console.log('[LandingPage] useEffect - Component mounted, current path:', window.location.pathname);
    return () => {
      // console.log('[LandingPage] Component unmounting');
    };
  }, []);

  const handleServiceClick = (intent: 'shuttle' | 'equipment' | 'marketplace') => {
    console.log('[LandingPage] Service clicked:', intent);
    
    // Check if user is authenticated (either platform or service)
    if (isAuthenticated) {
      // Navigate directly to service if authenticated
      if (intent === 'marketplace') {
        navigate('/app/marketplace');
      } else if (intent === 'shuttle') {
        navigate('/app/shuttle-booking');
      } else if (intent === 'equipment') {
        navigate('/app/reminders/dashboard');
      }
    } else {
      // Show authentication modal if not authenticated
      setServiceIntent(intent);
      setAuthModalOpen(true);
    }
  };

  const features = [
    {
      icon: <Groups sx={{ fontSize: 48 }} />,
      title: t('landing.features.clubMembership.title'),
      description: t('landing.features.clubMembership.description'),
      color: '#3388ff',
      onClick: () => navigate('/registrations'),
    },
    {
      icon: <Flight sx={{ fontSize: 48 }} />,
      title: t('landing.features.visitors.title'),
      description: t('landing.features.visitors.description'),
      color: '#00c853',
      onClick: () => navigate('/registrations'),
    },
    {
      icon: <DirectionsBus sx={{ fontSize: 48 }} />,
      title: t('landing.features.shuttle.title'),
      description: t('landing.features.shuttle.description'),
      color: '#ff4444',
      badge: 'BETA',
      onClick: () => handleServiceClick('shuttle'),
    },
    {
      icon: <BuildIcon sx={{ fontSize: 48 }} />,
      title: t('landing.features.reminders.title'),
      description: t('landing.features.reminders.description'),
      color: '#ff9800',
      badge: 'BETA',
      onClick: () => handleServiceClick('equipment'),
    },
    {
      icon: <Storefront sx={{ fontSize: 48 }} />,
      title: t('landing.features.marketplace.title'),
      description: t('landing.features.marketplace.description'),
      color: '#673ab7',
      badge: 'NEW & BETA',
      onClick: () => handleServiceClick('marketplace'),
    },
    {
      icon: <PhotoCameraIcon sx={{ fontSize: 48 }} />,
      title: t('landing.features.photoContest.title'),
      description: t('landing.features.photoContest.description'),
      color: '#9c27b0',
      badge: 'COMING SOON',
      onClick: undefined, // Coming soon
    },
  ];

  const handleCloseAuthModal = () => {
    setAuthModalOpen(false);
    setServiceIntent(null);
  };

  return (
    <>
      {/* Hero Section */}
      <Box
        sx={{
          backgroundImage: 'linear-gradient(rgba(102, 126, 234, 0.4), rgba(118, 75, 162, 0.4)), url(/RPP_3511.JPG)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: { xs: 'scroll', md: 'fixed' },
          backgroundRepeat: 'no-repeat',
          position: 'relative',
          overflow: 'hidden',
          pt: 8,
          pb: 12,
          minHeight: { xs: '80vh', md: 'auto' },
        }}
      >
        {/* Animated background elements */}
        <Box
          sx={{
            position: 'absolute',
            top: '10%',
            left: '5%',
            width: 300,
            height: 300,
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.1)',
            filter: 'blur(80px)',
            animation: 'float 8s ease-in-out infinite',
            '@keyframes float': {
              '0%, 100%': { transform: 'translateY(0) scale(1)' },
              '50%': { transform: 'translateY(-30px) scale(1.1)' },
            },
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: '10%',
            right: '10%',
            width: 250,
            height: 250,
            borderRadius: '50%',
            background: 'rgba(255, 0, 251, 0.1)',
            filter: 'blur(60px)',
            animation: 'float 10s ease-in-out infinite reverse',
          }}
        />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: 6,
              alignItems: 'center',
              animation: 'fadeInUp 0.8s ease-out',
              '@keyframes fadeInUp': {
                '0%': { opacity: 0, transform: 'translateY(50px)' },
                '100%': { opacity: 1, transform: 'translateY(0)' },
              },
            }}
          >
            {/* Left Content */}
            <Box>
              <Typography
                variant={isMobile ? 'h3' : 'h2'}
                sx={{
                  fontWeight: 900,
                  color: 'white',
                  mb: 3,
                  textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
                  fontFamily:
                    '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
                  fontSize: { xs: '2.5rem', sm: '3rem', md: '3.5rem' },
                  lineHeight: 1.2,
                  letterSpacing: '-0.02em',
                }}
              >
                {t('landing.hero.heading')}
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  color: 'rgba(255, 255, 255, 0.95)',
                  mb: 2,
                  fontWeight: 400,
                  lineHeight: 1.4,
                  fontFamily:
                    '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
                  fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' },
                }}
              >
                {t('landing.hero.description')}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: 'rgba(255, 255, 255, 0.85)',
                  mb: 4,
                  fontWeight: 400,
                  fontFamily:
                    '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
                  fontSize: { xs: '1rem', sm: '1.1rem' },
                }}
              >
                Powered by HFSS - HikeAndFly Software Solutions
              </Typography>

              {/* CTA Buttons */}
              <Box sx={{ mb: 4, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate('/registrations')}
                  sx={{
                    backgroundColor: '#ff00fb',
                    color: 'white',
                    px: 4,
                    py: 1.5,
                    fontWeight: 600,
                    fontSize: '1.1rem',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                    '&:hover': {
                      backgroundColor: '#cc00c8',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 6px 12px rgba(0,0,0,0.2)',
                    },
                  }}
                >
                  {t('landing.cta.pilotRegistration')}
                </Button>
                
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => navigate('/register')}
                  sx={{
                    borderColor: 'rgba(255, 255, 255, 0.7)',
                    color: 'white',
                    px: 3,
                    py: 1.3,
                    fontWeight: 500,
                    fontSize: '1rem',
                    backdropFilter: 'blur(10px)',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    '&:hover': {
                      borderColor: 'rgba(255, 255, 255, 0.9)',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      transform: 'translateY(-1px)',
                    },
                  }}
                >
                  {t('landing.cta.createYourClub')}
                </Button>
              </Box>

              {/* Trusted By Section */}
              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    mb: 2,
                    fontWeight: 500,
                    fontSize: '0.875rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                  }}
                >
                  {t('landing.trustedBy', 'Trusted by')}
                </Typography>
                <Box sx={{ 
                  display: 'flex', 
                  gap: 3,
                  alignItems: 'center',
                  flexWrap: 'wrap',
                }}>
                  <Box
                    component="a"
                    href="https://www.vllm.it"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      backgroundColor: 'rgba(255, 255, 255, 0.95)',
                      borderRadius: 2,
                      padding: '10px 20px',
                      display: 'flex',
                      alignItems: 'center',
                      transition: 'all 0.3s ease',
                      textDecoration: 'none',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        backgroundColor: 'rgba(255, 255, 255, 1)',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                        '& img': {
                          filter: 'grayscale(0.5)',
                        },
                      },
                    }}
                  >
                    <Box
                      component="img"
                      src="https://www.vllm.it/wp-content/uploads/2024/01/cropped-LOGO_GIALLO_PNG-removebg-preview.png"
                      alt="VLLM"
                      sx={{
                        height: 45,
                        width: 'auto',
                        objectFit: 'contain',
                        filter: 'grayscale(1) opacity(0.7)',
                        transition: 'filter 0.3s ease',
                      }}
                    />
                  </Box>
                  <Box
                    component="a"
                    href="https://www.volomania.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      backgroundColor: 'rgba(255, 255, 255, 0.95)',
                      borderRadius: 2,
                      padding: '10px 20px',
                      display: 'flex',
                      alignItems: 'center',
                      transition: 'all 0.3s ease',
                      textDecoration: 'none',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        backgroundColor: 'rgba(255, 255, 255, 1)',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                        '& img': {
                          filter: 'grayscale(0.5)',
                        },
                      },
                    }}
                  >
                    <Box
                      component="img"
                      src="https://static.wixstatic.com/media/702ba6_91ebb60aa10a4697bfdd2a4d6d20f693~mv2.png/v1/crop/x_0,y_662,w_3000,h_734/fill/w_336,h_82,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/Logo%20Volomania_edited.png"
                      alt="Volomania"
                      sx={{
                        height: 45,
                        width: 'auto',
                        objectFit: 'contain',
                        filter: 'grayscale(1) opacity(0.7)',
                        transition: 'filter 0.3s ease',
                      }}
                    />
                  </Box>
                  <Box
                    component="a"
                    href="https://www.fivl.it"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      backgroundColor: 'rgba(255, 255, 255, 0.95)',
                      borderRadius: 2,
                      padding: '10px 20px',
                      display: 'flex',
                      alignItems: 'center',
                      transition: 'all 0.3s ease',
                      textDecoration: 'none',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        backgroundColor: 'rgba(255, 255, 255, 1)',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                        '& img': {
                          filter: 'grayscale(0.5)',
                        },
                      },
                    }}
                  >
                    <Box
                      component="img"
                      src="https://www.fivl.it/templates/yootheme/cache/38/fivl-asd-3871641b.webp"
                      alt="FIVL ASD"
                      sx={{
                        height: 45,
                        width: 'auto',
                        objectFit: 'contain',
                        filter: 'grayscale(1) opacity(0.7)',
                        transition: 'filter 0.3s ease',
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* Right Visual - Empty space for balance */}
            <Box
              sx={{
                display: { xs: 'none', md: 'block' },
              }}
            />
          </Box>
        </Container>
        
        {/* HFSS Logo Animation - Bottom Right */}
        <Box
          sx={{
            position: 'absolute',
            bottom: { xs: 100, md: 80 },
            right: { xs: 20, md: 40 },
            display: { xs: 'none', sm: 'block' },
          }}
        >
          <Box
            sx={{
              width: { xs: 150, md: 200 },
              height: { xs: 150, md: 200 },
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              animation: 'pulse 4s ease-in-out infinite',
              '@keyframes pulse': {
                '0%, 100%': { transform: 'scale(1)' },
                '50%': { transform: 'scale(1.05)' },
              },
            }}
          >
            <img
              src={hfssLogo}
              alt="HFSS"
              style={{
                height: isMobile ? 60 : 80,
                filter: 'drop-shadow(0 8px 16px rgba(0,0,0,0.3))',
              }}
            />

            {/* Floating cards around logo */}
            <Box
              sx={{
                position: 'absolute',
                top: -15,
                right: 15,
                width: 60,
                height: 75,
                borderRadius: 2,
                background: 'white',
                boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                animation: 'floatCard 6s ease-in-out infinite',
                '@keyframes floatCard': {
                  '0%, 100%': { transform: 'translateY(0) rotate(-10deg)' },
                  '50%': { transform: 'translateY(-20px) rotate(-5deg)' },
                },
              }}
            >
              <CardMembership sx={{ fontSize: 30, color: '#3388ff' }} />
            </Box>

            <Box
              sx={{
                position: 'absolute',
                bottom: -10,
                left: 10,
                width: 60,
                height: 75,
                borderRadius: 2,
                background: 'white',
                boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                animation: 'floatCard 8s ease-in-out infinite reverse',
              }}
            >
              <Box
                sx={{
                  fontSize: 30,
                  color: '#ff00fb',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                🪂
              </Box>
            </Box>
          </Box>
        </Box>
        
        {/* Photo Credit */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 10,
            right: 10,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(10px)',
            borderRadius: 1,
            px: 1.5,
            py: 0.5,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: 'rgba(255, 255, 255, 0.9)',
              fontSize: '0.75rem',
              fontWeight: 500,
            }}
          >
            📸 Photo by Rookie Photo
          </Typography>
        </Box>
      </Box>

      {/* Features Section */}
      <Box sx={{ py: 12, backgroundColor: '#f5f5f5' }}>
        <Container maxWidth="lg">
          <Fade in timeout={1000}>
            <Typography
              variant="h2"
              align="center"
              sx={{
                fontWeight: 800,
                mb: 8,
                background: 'linear-gradient(45deg, #3388ff 30%, #ff00fb 90%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontFamily:
                  '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
                fontSize: { xs: '2.5rem', sm: '3rem', md: '3.5rem' },
                letterSpacing: '-0.02em',
              }}
            >
              {t('landing.features.title')}
            </Typography>
          </Fade>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
              },
              gap: 4,
            }}
          >
            {features.map((feature, index) => (
              <Card
                key={index}
                onClick={feature.onClick}
                sx={{
                  height: '100%',
                  background: 'white',
                  border: '1px solid rgba(0,0,0,0.1)',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  overflow: 'visible',
                  cursor: feature.onClick ? 'pointer' : 'default',
                  '&:hover': {
                    transform: 'translateY(-10px)',
                    boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                    borderColor: feature.color,
                  },
                }}
              >
                {feature.badge && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -10,
                      right: 16,
                      bgcolor: feature.color,
                      color: 'white',
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 1,
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                      zIndex: 1,
                    }}
                  >
                    {feature.badge}
                  </Box>
                )}
                <CardContent sx={{ textAlign: 'center', p: 4 }}>
                  <Box
                    sx={{
                      color: feature.color,
                      mb: 3,
                      display: 'flex',
                      justifyContent: 'center',
                    }}
                  >
                    {feature.icon}
                  </Box>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      mb: 2,
                      color: '#333',
                      fontFamily:
                        '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
                      fontSize: { xs: '1.25rem', sm: '1.5rem' },
                      letterSpacing: '-0.01em',
                    }}
                  >
                    {feature.title}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: '#666',
                      lineHeight: 1.7,
                      fontFamily:
                        '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
                      fontSize: { xs: '0.95rem', sm: '1rem' },
                    }}
                  >
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Club Managers Section */}
      <Box sx={{ py: 12, backgroundColor: 'white' }}>
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: 6,
              alignItems: 'center',
            }}
          >
            {/* Left Content */}
            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 800,
                  mb: 3,
                  background:
                    'linear-gradient(45deg, #3388ff 30%, #ff00fb 90%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontFamily:
                    '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
                  fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                  letterSpacing: '-0.02em',
                }}
              >
                {t('landing.clubManagers.title')}
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  color: '#666',
                  mb: 4,
                  fontWeight: 400,
                  lineHeight: 1.6,
                  fontFamily:
                    '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
                  fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' },
                }}
              >
                {t('landing.clubManagers.subtitle')}
              </Typography>
              <Box sx={{ mb: 4 }}>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 2.5,
                    color: '#555',
                    fontFamily:
                      '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
                    fontSize: { xs: '1rem', sm: '1.1rem' },
                    fontWeight: 500,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  ✓ {t('landing.clubManagers.benefits.digitalCards')}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 2.5,
                    color: '#555',
                    fontFamily:
                      '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
                    fontSize: { xs: '1rem', sm: '1.1rem' },
                    fontWeight: 500,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  ✓ {t('landing.clubManagers.benefits.automatedManagement')}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 2.5,
                    color: '#555',
                    fontFamily:
                      '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
                    fontSize: { xs: '1rem', sm: '1.1rem' },
                    fontWeight: 500,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  ✓ {t('landing.clubManagers.benefits.shuttleSystem')}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 2.5,
                    color: '#555',
                    fontFamily:
                      '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
                    fontSize: { xs: '1rem', sm: '1.1rem' },
                    fontWeight: 500,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  ✓ {t('landing.clubManagers.benefits.analytics')}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate('/register')}
                  startIcon={<Groups />}
                  sx={{
                    background:
                      'linear-gradient(45deg, #3388ff 30%, #66a3ff 90%)',
                    color: 'white',
                    px: 4,
                    py: 1.5,
                    fontWeight: 600,
                    boxShadow: '0 4px 15px rgba(51, 136, 255, 0.3)',
                    '&:hover': {
                      background:
                        'linear-gradient(45deg, #0066ff 30%, #3388ff 90%)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 6px 20px rgba(51, 136, 255, 0.4)',
                    },
                  }}
                >
                  {t('landing.clubManagers.getAccount')}
                </Button>
              </Box>
            </Box>

            {/* Right Visual */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                position: 'relative',
              }}
            >
              <Box
                sx={{
                  width: 400,
                  height: 400,
                  borderRadius: 3,
                  background:
                    'linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                  overflow: 'hidden',
                  boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                }}
              >
                {/* Mock Dashboard */}
                <Box
                  sx={{ position: 'absolute', top: 20, left: 20, right: 20 }}
                >
                  <Box
                    sx={{
                      height: 12,
                      borderRadius: 10,
                      background: '#3388ff',
                      mb: 2,
                    }}
                  />
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Box
                      sx={{
                        flex: 1,
                        height: 80,
                        borderRadius: 2,
                        background: 'white',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      }}
                    />
                    <Box
                      sx={{
                        flex: 1,
                        height: 80,
                        borderRadius: 2,
                        background: 'white',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      }}
                    />
                  </Box>
                </Box>

                {/* Center Icon */}
                <Groups
                  sx={{ fontSize: 120, color: '#3388ff', opacity: 0.2 }}
                />

                {/* Floating Elements */}
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 30,
                    right: 30,
                    width: 160,
                    height: 100,
                    borderRadius: 2,
                    background: 'white',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    animation: 'float 6s ease-in-out infinite',
                  }}
                >
                  <CardMembership sx={{ fontSize: 50, color: '#ff00fb' }} />
                </Box>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Race Organization Section */}
      <Box 
        sx={{ 
          py: 12, 
          backgroundColor: '#fafbfc',
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%231e3c72' fill-opacity='0.02'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            width: '80px',
            height: '4px',
            backgroundColor: '#ffd700',
            borderRadius: 2,
          }
        }}
      >
        <Container maxWidth="lg">
          {/* Section Title */}
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                mb: 3,
                background: 'linear-gradient(45deg, #3388ff 30%, #ff00fb 90%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontFamily:
                  '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
                fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                letterSpacing: '-0.02em',
              }}
            >
              Take Your Competitions to the Next Level
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: '#666',
                maxWidth: 600,
                mx: 'auto',
                fontWeight: 400,
                lineHeight: 1.6,
              }}
            >
              Professional race organization and scoring platform for hike & fly competitions
            </Typography>
          </Box>
          <Card
            sx={{
              background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
              color: 'white',
              overflow: 'hidden',
              position: 'relative',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: '0 10px 30px rgba(30, 60, 114, 0.15)',
              border: '2px solid transparent',
              backgroundClip: 'padding-box',
              '&:hover': {
                transform: 'translateY(-8px) scale(1.02)',
                boxShadow: '0 20px 50px rgba(30, 60, 114, 0.25)',
                border: '2px solid rgba(255, 215, 0, 0.3)',
              },
            }}
            onClick={() => window.open('https://www.hikeandfly.app', '_blank')}
          >
            {/* Background decoration */}
            <Box
              sx={{
                position: 'absolute',
                top: -50,
                right: -50,
                width: 200,
                height: 200,
                borderRadius: '50%',
                background: 'rgba(255, 255, 255, 0.05)',
                filter: 'blur(40px)',
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                bottom: -100,
                left: -100,
                width: 300,
                height: 300,
                borderRadius: '50%',
                background: 'rgba(255, 255, 255, 0.03)',
                filter: 'blur(60px)',
              }}
            />
            
            <CardContent sx={{ p: { xs: 4, md: 6 }, position: 'relative', zIndex: 1 }}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                  gap: 4,
                  alignItems: 'center',
                }}
              >
                {/* Left Content */}
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                    <EmojiEvents sx={{ fontSize: 56, color: '#ffd700' }} />
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 800,
                        fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                        letterSpacing: '-0.02em',
                      }}
                    >
                      {t('landing.raceOrganization.title')}
                    </Typography>
                  </Box>
                  
                  <Typography
                    variant="h5"
                    sx={{
                      mb: 3,
                      opacity: 0.95,
                      fontWeight: 400,
                      lineHeight: 1.6,
                      fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' },
                    }}
                  >
                    {t('landing.raceOrganization.subtitle')}
                  </Typography>
                  
                  <Box sx={{ mb: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Speed sx={{ fontSize: 24 }} />
                      <Typography variant="body1" sx={{ fontSize: '1.1rem' }}>
                        {t('landing.raceOrganization.features.realTimeScoring')}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Timeline sx={{ fontSize: 24 }} />
                      <Typography variant="body1" sx={{ fontSize: '1.1rem' }}>
                        {t('landing.raceOrganization.features.trackManagement')}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Groups sx={{ fontSize: 24 }} />
                      <Typography variant="body1" sx={{ fontSize: '1.1rem' }}>
                        {t('landing.raceOrganization.features.pilotRegistration')}
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Button
                    variant="contained"
                    size="large"
                    sx={{
                      backgroundColor: '#ffd700',
                      color: '#1e3c72',
                      px: 4,
                      py: 1.5,
                      fontWeight: 600,
                      fontSize: '1.1rem',
                      boxShadow: '0 4px 15px rgba(255, 215, 0, 0.3)',
                      '&:hover': {
                        backgroundColor: '#ffed4e',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 6px 20px rgba(255, 215, 0, 0.4)',
                      },
                    }}
                  >
                    {t('landing.raceOrganization.cta')}
                  </Button>
                </Box>
                
                {/* Right Visual */}
                <Box
                  sx={{
                    display: { xs: 'none', md: 'flex' },
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <Box
                    sx={{
                      width: 300,
                      height: 300,
                      borderRadius: 3,
                      background: 'rgba(255, 255, 255, 0.1)',
                      backdropFilter: 'blur(10px)',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '2px solid rgba(255, 255, 255, 0.2)',
                    }}
                  >
                    <Typography
                      variant="h1"
                      sx={{
                        fontWeight: 900,
                        fontSize: '4rem',
                        color: '#ffd700',
                        textShadow: '0 4px 8px rgba(0,0,0,0.3)',
                      }}
                    >
                      HF
                    </Typography>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 600,
                        color: 'white',
                        mt: 1,
                      }}
                    >
                      HikeAndFly.app
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'rgba(255, 255, 255, 0.8)',
                        mt: 2,
                        textAlign: 'center',
                        px: 3,
                      }}
                    >
                      Professional Race Scoring Platform
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Container>
      </Box>

      {/* Weather Widget Section */}
      <Box sx={{ py: 8, backgroundColor: '#f5f5f5' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              variant="h3"
              align="center"
              sx={{
                fontWeight: 700,
                mb: 3,
                background: 'linear-gradient(45deg, #3388ff 30%, #ff00fb 90%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontFamily:
                  '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
                fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                letterSpacing: '-0.02em',
              }}
            >
              {t('landing.weather.title')}
            </Typography>
            <Typography
              variant="h5"
              sx={{
                color: '#666',
                fontWeight: 400,
                lineHeight: 1.6,
                fontFamily:
                  '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
                fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' },
              }}
            >
              {t('landing.weather.subtitle')}
            </Typography>
          </Box>
          <WindyWidget />
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          py: 10,
          background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Background decoration */}
        <Box
          sx={{
            position: 'absolute',
            top: -100,
            right: -100,
            width: 400,
            height: 400,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(51, 136, 255, 0.1) 0%, transparent 70%)',
            filter: 'blur(40px)',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: -150,
            left: -150,
            width: 500,
            height: 500,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255, 0, 251, 0.08) 0%, transparent 70%)',
            filter: 'blur(40px)',
          }}
        />
        
        <Container maxWidth="lg">
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            {/* Logo and Title */}
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Box
                sx={{
                  display: 'inline-block',
                  padding: 2.5,
                  borderRadius: 3,
                  background: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  mb: 4,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
                  },
                }}
              >
                <img
                  src={hfssLogo}
                  alt="HFSS"
                  style={{
                    height: 90,
                    display: 'block',
                    filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.2))',
                  }}
                />
              </Box>
              
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 800,
                  mb: 3,
                  fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                  background: 'linear-gradient(135deg, #ffffff 0%, #e0e0e0 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  letterSpacing: '-0.02em',
                }}
              >
                {t('landing.readyToModernize')}
              </Typography>
            </Box>

            {/* Stats Grid */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                gap: 4,
                mb: 6,
              }}
            >
              <Box
                sx={{
                  textAlign: 'center',
                  p: 3,
                  borderRadius: 2,
                  background: 'rgba(255, 255, 255, 0.03)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    background: 'rgba(255, 255, 255, 0.06)',
                    transform: 'translateY(-2px)',
                    borderColor: 'rgba(51, 136, 255, 0.3)',
                  },
                }}
              >
                <Typography
                  variant="h2"
                  sx={{
                    fontWeight: 800,
                    fontSize: '2.5rem',
                    background: 'linear-gradient(45deg, #3388ff 30%, #66a3ff 90%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    mb: 1,
                  }}
                >
                  50+
                </Typography>
                <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  {t('landing.stats.activeClubs')}
                </Typography>
              </Box>

              <Box
                sx={{
                  textAlign: 'center',
                  p: 3,
                  borderRadius: 2,
                  background: 'rgba(255, 255, 255, 0.03)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    background: 'rgba(255, 255, 255, 0.06)',
                    transform: 'translateY(-2px)',
                    borderColor: 'rgba(255, 0, 251, 0.3)',
                  },
                }}
              >
                <Typography
                  variant="h2"
                  sx={{
                    fontWeight: 800,
                    fontSize: '2.5rem',
                    background: 'linear-gradient(45deg, #ff00fb 30%, #ff66fc 90%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    mb: 1,
                  }}
                >
                  5000+
                </Typography>
                <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  {t('landing.stats.activePilots')}
                </Typography>
              </Box>

              <Box
                sx={{
                  textAlign: 'center',
                  p: 3,
                  borderRadius: 2,
                  background: 'rgba(255, 255, 255, 0.03)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    background: 'rgba(255, 255, 255, 0.06)',
                    transform: 'translateY(-2px)',
                    borderColor: 'rgba(76, 175, 80, 0.3)',
                  },
                }}
              >
                <Typography
                  variant="h2"
                  sx={{
                    fontWeight: 800,
                    fontSize: '2.5rem',
                    background: 'linear-gradient(45deg, #4caf50 30%, #8bc34a 90%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    mb: 1,
                  }}
                >
                  100%
                </Typography>
                <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  {t('landing.stats.fullyDigital')}
                </Typography>
              </Box>
            </Box>

            {/* Description */}
            <Typography
              variant="h6"
              align="center"
              sx={{
                mb: 5,
                color: 'rgba(255, 255, 255, 0.85)',
                maxWidth: 800,
                mx: 'auto',
                fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' },
                lineHeight: 1.8,
                fontWeight: 400,
              }}
            >
              {t('landing.platformDescription')}
            </Typography>

            {/* CTA Buttons */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center',
              gap: 3,
              flexWrap: 'wrap',
            }}>
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate('/register')}
                sx={{
                  background: 'linear-gradient(45deg, #3388ff 30%, #66a3ff 90%)',
                  color: 'white',
                  py: 1.8,
                  px: 5,
                  fontWeight: 600,
                  fontSize: '1.1rem',
                  borderRadius: 2,
                  boxShadow: '0 8px 24px rgba(51, 136, 255, 0.4)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #0066ff 30%, #3388ff 90%)',
                    transform: 'translateY(-3px)',
                    boxShadow: '0 12px 32px rgba(51, 136, 255, 0.5)',
                  },
                }}
              >
                {t('landing.clubManagers.getAccount')}
              </Button>
              
              <Button
                variant="outlined"
                size="large"
                onClick={() => window.open('https://www.hikeandfly.app', '_blank')}
                sx={{
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                  color: 'white',
                  py: 1.8,
                  px: 5,
                  fontWeight: 600,
                  fontSize: '1.1rem',
                  borderRadius: 2,
                  borderWidth: 2,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: 'white',
                    backgroundColor: 'rgba(255, 255, 255, 0.08)',
                    transform: 'translateY(-3px)',
                    borderWidth: 2,
                  },
                }}
              >
                {t('landing.visitHikeAndFly')}
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Reminders Modal */}
      <RemindersModal
        open={remindersModalOpen}
        onClose={() => setRemindersModalOpen(false)}
      />

      {/* Service Auth Modal */}
      <ServiceAuthModal
        open={authModalOpen}
        onClose={handleCloseAuthModal}
        intent={serviceIntent}
      />
    </>
  );
};

export default LandingPage;
